import React from 'react';
import { Box, Typography, Skeleton, Card, useTheme } from '@mui/material';
import { tokens } from '../../../theme';
import ChatIcon from '@mui/icons-material/Chat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Donation } from './types';

interface SupporterWallProps {
  donations: Donation[];
  loading: boolean;
}

const Community: React.FC<SupporterWallProps> = ({ donations, loading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const formatDate = (ts: number) => {
    try {
      return new Date(ts).toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <Box component="section" id="community" sx={{ py: 12, bgcolor: colors.primary[900] }}>
      <Box sx={{ maxWidth: 1280, mx: "auto", px: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { md: "flex-end" },
            mb: 6,
            gap: 3,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: "1.8rem",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <ChatIcon sx={{ color: "primary.main" }} /> Supporter Wall
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              The community of light that makes this possible.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: colors.primary[800],
              px: 3,
              py: 1.5,
              borderRadius: 4,
            }}
          >
            <TrendingUpIcon sx={{ color: "success.main" }} />
            <Box>
              <Typography variant="subtitle2">Live Pulse</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                New donation every ~4h
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              lg: "1fr 1fr 1fr",
            },
            gap: 3,
          }}
        >
          {loading
            ? Array(6)
                .fill(0)
                .map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={128}
                    sx={{ borderRadius: 4 }}
                  />
                ))
            : donations.map((d) => (
                <Card
                  key={d.id}
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: colors.primary[800],
                    borderColor: colors.primary[300],
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "#fed7aa" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: colors.primary[900],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "primary.main",
                        fontWeight: 700,
                        fontSize: 14,
                        border: `1px solid ${colors.primary[300]}`,
                        transition: "all 0.2s",
                        "&:hover": { bgcolor: "primary.main", color: "white" },
                      }}
                    >
                      ${d.amount}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {d.name}
                      </Typography>
                      <Typography variant="subtitle2">
                        {formatDate(d.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                  {d.message && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      &apos;{d.message}&apos;
                    </Typography>
                  )}
                </Card>
              ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Community;
